import {
  MyBookings,
  AuthRegister,
  AuthUserProfile,
  RegisterQueryData,
  ChangePasswordQueryData,
} from './authTypes';
import {
  LOGIN,
  LOGOUT,
  INIT_USER,
  SET_MFA_DATA,
  SET_MY_PROFILE,
  AuthThunkAction,
  CLEAR_USER_DATA,
  SET_MY_BOOKINGS,
  loginActionType,
  logoutActionType,
  UPDATE_MY_PROFILE,
  initUserActionType,
  FETCH_REGISTER_DATA,
  setMfaDataActionType,
  SET_EDIT_PASSWORD_DATA,
  setMyProfileActionType,
  clearUserDataActionType,
  SET_REGISTER_QUERY_DATA,
  SET_RESET_PASSWORD_DATA,
  setMyBookingsActionType,
  SET_RESET_PASSWORD_EMAIL,
  UpdateMyProfileActionType,
  fetchRegisterDataActionType,
  SET_PHONE_CONFIRMATION_DATA,
  setEditPasswordDataActionType,
  SET_CHANGE_PASSWORD_QUERY_DATA,
  SetPhoneConfirmationActionType,
  setRegisterQueryDataActionType,
  setResetPasswordDataActionType,
  setResetPasswordEmailActionType,
  setChangePasswordQueryDataActionType,
} from './authActionTypes';
import { TFunction } from 'i18next';
import AuthApi from '../../API/auth/Auth.api';
import { AUTH_NAMESPACE } from './authReducer';
import localStorageService from '../../services/local-storage';
import { startLoaderThunk, stopLoaderThunk } from '../modal/modalActions';
import { LoginRequestDto } from '../../API/auth/dto/request/login-request.dto';
import { RegisterRequestDto } from '../../API/auth/dto/request/register-request.dto';
import { EditPasswordRequestDto } from '../../API/auth/dto/request/edit-password.request.dto';
import { LoginWithMfaRequestDto } from '../../API/auth/dto/request/login-with-mfa-request.dto';
import { ResetPasswordRequestDto } from '../../API/auth/dto/request/reset-password-request.dto';
import { ChangePasswordRequestDto } from '../../API/auth/dto/request/change-password.request.dto';
import { UpdateUserTermsDto } from '../../API/auth/dto/request/update-user-terms.dto';
import { noLoginByTokenLogicRoutes } from './no-login-routes';
import LocalStorage from '../../services/local-storage';

export function initUserAction(): initUserActionType {
  return {
    type: INIT_USER,
  };
}

export function clearUserDataAction(): clearUserDataActionType {
  return {
    type: CLEAR_USER_DATA,
  };
}

export function setEditPasswordDataAction(data: {
  email?: string;
  oldPassword?: string;
  accessToken?: string;
}): setEditPasswordDataActionType {
  return {
    type: SET_EDIT_PASSWORD_DATA,
    payload: data,
  };
}

export function setPhoneConfirmationAction(data: {
  needPhoneConfirmation?: boolean;
  phoneNumber?: string;
  email?: string;
  refreshToken?: string;
  password?: string;
}): SetPhoneConfirmationActionType {
  return {
    type: SET_PHONE_CONFIRMATION_DATA,
    payload: data,
  };
}

export function fetchRegisterDataAction(
  data: AuthRegister
): fetchRegisterDataActionType {
  return {
    type: FETCH_REGISTER_DATA,
    payload: data,
  };
}

export function loginAction(data: AuthUserProfile): loginActionType {
  return {
    type: LOGIN,
    payload: data,
  };
}

export function setMyBookingsAction(
  data: MyBookings[]
): setMyBookingsActionType {
  return {
    type: SET_MY_BOOKINGS,
    payload: data,
  };
}

export function setMyProfileAction(
  data: AuthUserProfile
): setMyProfileActionType {
  return {
    type: SET_MY_PROFILE,
    payload: data,
  };
}

export function setMfaDataAction(
  session: string,
  email: string
): setMfaDataActionType {
  return {
    type: SET_MFA_DATA,
    payload: { session, email },
  };
}

export function logoutAction(): logoutActionType {
  return {
    type: LOGOUT,
  };
}

export function setRegisterQueryDataAction(
  data: RegisterQueryData
): setRegisterQueryDataActionType {
  return {
    type: SET_REGISTER_QUERY_DATA,
    payload: data,
  };
}

export function setChangePasswordQueryDataAction(
  data: ChangePasswordQueryData
): setChangePasswordQueryDataActionType {
  return {
    type: SET_CHANGE_PASSWORD_QUERY_DATA,
    payload: data,
  };
}

export function updateMyProfileAction(
  data: AuthUserProfile
): UpdateMyProfileActionType {
  return {
    type: UPDATE_MY_PROFILE,
    payload: data,
  };
}

export function setResetPasswordEmailAction(
  email: string
): setResetPasswordEmailActionType {
  return {
    type: SET_RESET_PASSWORD_EMAIL,
    payload: { email },
  };
}

export function setResetPasswordDataAction(
  email: string,
  code: string
): setResetPasswordDataActionType {
  return {
    type: SET_RESET_PASSWORD_DATA,
    payload: { email, code },
  };
}

export function logoutThunk(): AuthThunkAction<Promise<boolean>> {
  return async (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatch(logoutAction());
      localStorageService.removeAllTokens();
      dispatch(initUserAction());
      resolve(true);
    });
  };
}

export function clearUserDataThunk(): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    return new Promise((a, b) => {
      dispatch(clearUserDataAction());
      localStorageService.removeAllTokens();
      dispatch(initUserAction());
    });
  };
}

export function registerThunk(
  data: RegisterRequestDto
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return AuthApi.register(data)
      .then((response) => {
        dispatch(clearUserDataThunk());
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function setResetPasswordEmailThunk(
  email: string
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return AuthApi.generateResetCode(email)
      .then((response) => {})
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function resetPasswordThunk(
  newPassword: string
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    const resetPasswordState = getState()['AUTH_NAMESPACE'].resetPassword;
    const data: ResetPasswordRequestDto = {
      email: resetPasswordState?.email!,
      code: resetPasswordState?.code!,
      newPassword,
    };
    return AuthApi.resetPassword(data)
      .then((response) => {})
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function loginByTokenThunk(
  force?: boolean
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    await dispatch(startLoaderThunk());
    const path = window.location.href;

    if (!force && noLoginByTokenLogicRoutes.some((str) => path.includes(str))) {
      dispatch(initUserAction());
      return await dispatch(stopLoaderThunk());
    }
    const jwtToken = await localStorageService.getJWTToken();

    try {
      if (jwtToken) {
        await dispatch(getMyProfile());
      }
    } catch (error) {
      console.log('Failed to login');
    }

    dispatch(initUserAction());
    await dispatch(stopLoaderThunk());
  };
}

export function loginThunk(
  data: LoginRequestDto
): AuthThunkAction<Promise<void>> {
  return async (dispatch) => {
    await dispatch(startLoaderThunk());
    try {
      const loginResponse = await AuthApi.login(data);
      const tokens = loginResponse.data.tokens;
      localStorageService.setTokens(tokens?.refreshToken!, tokens?.jwtToken!);
      await dispatch(getMyProfile());
      dispatch(initUserAction());
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function loginMFAThunk(
  data: LoginWithMfaRequestDto
): AuthThunkAction<Promise<string | void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    const mfaData = getState()[AUTH_NAMESPACE].mfaData;
    data.session = mfaData.session;
    data.email = mfaData.email;

    return await AuthApi.loginWithMFA(data)
      .then(async (response) => {
        if (response?.data?.tokens) {
          localStorageService.setTokens(
            response?.data?.tokens?.refreshToken,
            response?.data?.tokens?.jwtToken
          );
          await dispatch(getMyProfile());
        }
      })
      .finally(() => {
        dispatch(initUserAction());
        dispatch(stopLoaderThunk());
      });
  };
}

export function changePasswordThunk(
  data: ChangePasswordRequestDto
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    await dispatch(startLoaderThunk());
    const response = await AuthApi.changePassword(data);
    const tokens = response.data.tokens;
    LocalStorage.setTokens(tokens.refreshToken, tokens.jwtToken);
    return await dispatch(stopLoaderThunk());
  };
}

export function getMyProfile(
  noLoader?: boolean
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    if (noLoader) {
      const response = await AuthApi.getMyProfile();
      dispatch(setMyProfileAction(response.data));
      return;
    }
    await dispatch(startLoaderThunk());
    const response = await AuthApi.getMyProfile();
    dispatch(setMyProfileAction(response.data));
    return await dispatch(stopLoaderThunk());
  };
}

export function updateMyProfile(
  user: AuthUserProfile,
  callBack: (isSuccessful: boolean, s: string) => void,
  t: TFunction
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    const authState = getState()['AUTH_NAMESPACE'];
    if (!authState.profile?.id) {
      return;
    }

    const updatedUser = {
      ...user,
      full_name: user.fullName,
      job_title: user.jobTitle,
      social_media: user.socialMedia ? { ...user.socialMedia } : undefined,
      company: user.companyName,
    };

    return AuthApi.updateMyProfile(updatedUser)
      .then(() => {
        dispatch(getMyProfile());
        callBack(true, t('PROFILE_UPDATE_SUCCESSFULLY'));
      })
      .catch((error) => {
        callBack(false, error?.response?.data?.error?.message);
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function UploadUserImageThunk(
  data: any,
  callback: any
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    return AuthApi.uploadUserImage(data)
      .then(async (response) => {
        callback(response.data.imageUrl, null);
      })
      .catch((error) => {
        callback(
          null,
          error?.response?.data?.error?.message || 'Something went wrong...'
        );
      });
  };
}

export function GetConfirmationPhoneCodeThunk(
  phone: string
): AuthThunkAction<Promise<any>> {
  return async (dispatch, getState) => {
    try {
      await dispatch(startLoaderThunk());

      const email = await localStorageService.getUserEmailFromToken();
      const refreshToken = localStorageService.getRefreshToken();
      if (!email || !refreshToken) {
        throw new Error('Failed to confirm phone. No valid tokens');
      }
      await AuthApi.getPhoneConfirmationCode({
        email,
        phone,
        refreshToken,
      });
      dispatch(setPhoneConfirmationAction({ phoneNumber: phone }));
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function verifyPhoneByCodyThunk(
  code: string
): AuthThunkAction<Promise<any>> {
  return async (dispatch, getState) => {
    try {
      await dispatch(startLoaderThunk());

      const email = await localStorageService.getUserEmailFromToken();
      const refreshToken = localStorageService.getRefreshToken();
      if (!email || !refreshToken) {
        throw new Error('Failed to confirm phone. No valid tokens');
      }

      const response = await AuthApi.verifyPhone({
        code,
        email,
        refreshToken,
      });
      const tokens = response.data.tokens;
      localStorageService.setTokens(tokens.refreshToken, tokens.jwtToken);
      await dispatch(getMyProfile());
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function editPasswordThunk(
  data: EditPasswordRequestDto
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState()[AUTH_NAMESPACE].editPasswordData;
    dispatch(startLoaderThunk()).finally();
    return AuthApi.editPassword({ ...data, accessToken: state.accessToken })
      .then(() => {
        dispatch(
          setEditPasswordDataAction({
            email: '',
            oldPassword: '',
            accessToken: '',
          })
        );
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function approveTermsThunk(
  data: UpdateUserTermsDto
): AuthThunkAction<Promise<void>> {
  return async (dispatch, getState) => {
    const accessToken = await localStorageService.getJWTToken();
    const refreshToken = localStorageService.getRefreshToken();
    await dispatch(startLoaderThunk());
    try {
      if (!accessToken || !refreshToken) {
        throw new Error('Failed to approve terms. No tokens');
      }
      const termsResponse = await AuthApi.approveTerms({
        ...data,
        accessToken,
        refreshToken,
      });
      const tokens = termsResponse.data.tokens;
      localStorageService.setTokens(tokens.refreshToken, tokens.jwtToken);

      await dispatch(getMyProfile(true));
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}
